/**
 * Модуль авторизации
 */
import { registerUser, loginUser } from './api.js';
import { resetCachedUserProgress } from './userconfig.js';

class Auth {
    constructor() {
        this.currentUser = null;
        this.isAuthenticated = false;
        this.isGuest = false;
        this.loadFromStorage();
    }

    loadFromStorage() {
        try {
            const stored = localStorage.getItem('auth_user');
            if (stored) {
                this.currentUser = JSON.parse(stored);
                this.isAuthenticated = true;
                this.isGuest = this.currentUser.is_guest || false;
            }
        } catch (error) {
            console.error('Ошибка при загрузке данных авторизации:', error);
            this.clearAuth();
        }
    }
    
    // Создание гостевого пользователя (только локально, БЕЗ сохранения в БД)
    createGuestUser() {
        try {
            // Генерируем случайное имя guest_XXXX
            const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
            const guestUsername = `guest_${randomNum}`;
            
            // Создаём локального гостя БЕЗ сохранения на сервере
            this.currentUser = {
                id: null, // ID будет null пока не сохранится на сервере
                name: guestUsername,
                is_guest: true
            };
            this.isAuthenticated = true;
            this.isGuest = true;
            this.saveToStorage();
            console.log('Local guest user created:', guestUsername);
            return { success: true, user: this.currentUser };
        } catch (error) {
            console.error('Error creating guest user:', error);
            return { success: false, error: error.message };
        }
    }
    
    // Преобразование гостя в постоянного пользователя (регистрация на сервере)
    async convertGuestToPermanent(progressData) {
        if (!this.isGuest || !this.currentUser) {
            console.warn('convertGuestToPermanent: not a guest user');
            return { success: false, error: 'Не гостевой пользователь' };
        }
        
        try {
            console.log('Converting guest to permanent user:', this.currentUser.name);
            
            // Генерируем случайный пароль для гостя
            const randomPassword = Math.random().toString(36).substring(2, 15);
            
            // Регистрируем пользователя на сервере
            console.log('Registering user on server...');
            const result = await registerUser(this.currentUser.name, randomPassword);
            console.log('Registration result:', result);
            
            if (result && result.success) {
                // Входим под новым пользователем
                console.log('Logging in with new credentials...');
                const loginResult = await loginUser(this.currentUser.name, randomPassword);
                console.log('Login result:', loginResult);
                
                if (loginResult && loginResult.success && loginResult.user) {
                    this.currentUser = loginResult.user;
                    this.currentUser.is_guest = false; // Помечаем как не гостя
                    this.isAuthenticated = true;
                    this.isGuest = false;
                    this.saveToStorage();
                    console.log('✅ Guest converted to permanent user, new ID:', this.currentUser.id);
                    return { success: true, user: this.currentUser };
                } else {
                    throw new Error('Login failed after registration');
                }
            } else {
                throw new Error('Registration failed: ' + (result ? result.error : 'no result'));
            }
        } catch (error) {
            console.error('❌ Error converting guest:', error);
            return { success: false, error: error.message };
        }
    }

    saveToStorage() {
        if (this.currentUser) {
            localStorage.setItem('auth_user', JSON.stringify(this.currentUser));
        } else {
            localStorage.removeItem('auth_user');
        }
    }

    clearAuth() {
        this.currentUser = null;
        this.isAuthenticated = false;
        this.isGuest = false; // Сбрасываем флаг гостя
        localStorage.removeItem('auth_user');
        
        // Очищаем все пользовательские данные из localStorage
        // Прогресс игры
        localStorage.removeItem('guitar_tuner_progress');
        
        // Настройки гитарных эффектов
        localStorage.removeItem('guitarEffectsSettings');
        
        // Настройки тюнера
        localStorage.removeItem('guitarTunerSettings');
        
        // Статус прохождения туториала
        localStorage.removeItem('tutorialCompleted');
        
        // Очищаем кэш прогресса пользователя в памяти
        resetCachedUserProgress();
        
        console.log('All user data cleared from localStorage and cache');
    }

    async register(username, password) {
        try {
            const result = await registerUser(username, password);
            if (result && result.success) {
                return {
                    success: true,
                    message: result.message || 'Регистрация успешна'
                };
            }
            throw new Error('Ошибка при регистрации');
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Ошибка при регистрации'
            };
        }
    }

    async login(username, password) {
        try {
            const result = await loginUser(username, password);
            if (result && result.success && result.user) {
                this.currentUser = result.user;
                this.currentUser.is_guest = false; // Помечаем как не гостя
                this.isAuthenticated = true;
                this.isGuest = false; // Сбрасываем флаг гостя при успешном входе
                this.saveToStorage();
                return {
                    success: true,
                    user: result.user
                };
            }
            throw new Error('Ошибка при входе');
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Ошибка при входе'
            };
        }
    }

    logout() {
        this.clearAuth();
    }

    getCurrentUser() {
        return this.currentUser;
    }

    getUserId() {
        return this.currentUser ? this.currentUser.id : null;
    }

    isLoggedIn() {
        return this.isAuthenticated && this.currentUser !== null;
    }

    isAdmin() {
        return this.isLoggedIn() && this.currentUser && this.currentUser.name === 'admin';
    }

    getUsername() {
        return this.currentUser ? this.currentUser.name : null;
    }
    
    isGuestUser() {
        return this.isGuest;
    }
}

// Создаем экземпляр
const auth = new Auth();

export default auth;

